import { v4 as uuid } from "uuid";
import { CalloutType, HeaderPositionType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class QuadrantChartCategory extends BaseCategory {
    get name() {
        return "Quadrant Chart";
    }

    get type() {
        return SlideCategories.QUADRANT_CHART;
    }

    get icon() {
        return null;
    }

    get description() {
        return "The content represents items positioned on a 2D quadrant chart. Use this for competitive analysis, priority matrices, positioning maps, or any content where items are plotted against two dimensions (e.g., effort vs. impact, cost vs. value, risk vs. reward).";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Create a bubble chart where items are positioned on a 2D plot based on two key dimensions.
        Provide the axis labels, data points with their positions (0-100 for both x and y), bubble sizes, and labels.
        Higher Y values should appear higher on the chart (top = 100, bottom = 0).
        Higher X values should appear further right on the chart (left = 0, right = 100).`;
    }

    getImagePrompt() {
        return false;
    }

    getPromptSchema() {
        return {
            xAxisLabel: "label for the horizontal (x) axis",
            yAxisLabel: "label for the vertical (y) axis",
            showQuadrants: "optional boolean: true to show dividing lines creating 4 quadrants (default: false)",
            quadrantLabels: "optional array of 4 strings: labels for each quadrant [topLeft, topRight, bottomLeft, bottomRight] - only if showQuadrants is true",
            items: [{
                label: "text label for the data point",
                icon: "optional icon name for the data point",
                x: "horizontal position from 0-100 (0 = left, 100 = right)",
                y: "vertical position from 0-100 (0 = bottom, 100 = top)",
                size: "bubble size from 10-200",
                emphasized: "optional boolean: true to emphasize this item visually (default: false)",
            }],
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    validateGeneratedModel(model) {
        if (!this.validateArray(model.items)) {
            return false;
        }
        return true;
    }

    getSlideModels(model) {
        let models = [
            this.buildQuadrantChart(model)
        ];
        return models;
    }

    buildQuadrantChart(model) {
        let builder = new SlideModelBuilder("xygraph", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        // Convert items to the XYGraph data format
        const data = (model.items || []).map((item, index) => ({
            id: uuid(),
            label: item.label || "Item",
            // Convert 0-100 scale to 0-1 for XYGraph
            // Invert Y so that higher values appear higher on the chart
            x: Math.min(Math.max((item.x || 50) / 100, 0), 1),
            y: Math.min(Math.max((100 - (item.y || 50)) / 100, 0), 1), // Invert Y axis
            // size: Math.min(Math.max(item.size || 100, 10), 400),
            size: 20,
            color: null,
            emphasized: item.emphasized || false,
            icon: item.icon || null,
        }));

        // Build the primary XYGraph element
        const showQuadrants = model.showQuadrants || false;
        const quadrantLabels = model.quadrantLabels || ["", "", "", ""];

        let primary = {
            collectionColor: "colorful",
            yAxisLabel: model.yAxisLabel || "Y Axis",
            xAxisLabel: model.xAxisLabel || "X Axis",
            showArrowHeads: true,
            showAxis: true,
            axisPosition: "left",
            showAxisLabels: true,
            showGrid: true,
            gridSize: 34,
            showQuadrants: showQuadrants,
            showQuadrantLabels: showQuadrants && quadrantLabels.some(l => l),
            label1: showQuadrants ? (quadrantLabels[0] || "") : "",
            label2: showQuadrants ? (quadrantLabels[1] || "") : "",
            label3: showQuadrants ? (quadrantLabels[2] || "") : "",
            label4: showQuadrants ? (quadrantLabels[3] || "") : "",
            data: data,
            elements: data.map(item => ({
                dataId: item.id,
                type: "callout",
                calloutType: item.icon ? CalloutType.CONTENT_AND_TEXT : CalloutType.FLEX_CIRCLE,
                decorationStyle: (data.every(d => !d.emphasized) || item.emphasized) ? "filled" : "muted",
                ...builder.createIcon(item.icon),
                frameType: item.icon ? "circle" : null,
                textDirection: "bottom"
            }))
        };

        builder.createPrimary(primary);

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }
}
